import { useState, type FormEvent } from 'react';
import { redeemCode } from '../../api/billing';
import {
  REDEEM_WORDS,
  redeemFailureMessage,
  redeemSuccessMessage,
} from '@/components/account/accountView';
import { Button } from '../../ui';

export interface Redeemed {
  /** The balance after the code, when the server said it. */
  creditsRemaining?: number;
  tier?: string;
}

/**
 * Redeem a promo code — the phone's Account → Credits field, in the browser.
 *
 * The web is where codes are redeemed now: the iOS app hides its field (App Store 3.1.1, see
 * `showsRedeemCode`), so without this a code handed to somebody on an iPhone had nowhere to go.
 *
 * Same route, same words: `POST /redeem` through `redeemCode`, and every answer read through the
 * phone's own `redeemSuccessMessage` / `redeemFailureMessage`. The phone shows its answer in an
 * alert with a title; here the title leads the line, and a refusal is an alert while a success is
 * a status, so a screen reader interrupts only for the one that needs acting on.
 *
 * `tier` is the plan BEFORE the code, which is what lets the answer say "You're now on the Pro
 * plan" only when that is news. `onRedeemed` hands the new balance to the screen that shows it.
 */
export default function RedeemCode({
  tier,
  onRedeemed,
}: {
  tier?: string | null;
  onRedeemed?: (result: Redeemed) => void;
}) {
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [answer, setAnswer] = useState<{ ok: boolean; message: string } | null>(null);
  const typed = code.trim();

  async function submit(e: FormEvent): Promise<void> {
    e.preventDefault();
    if (!typed || busy) return;
    setBusy(true);
    setAnswer(null);
    try {
      const result = await redeemCode(typed);
      if (result.status === 'redeemed') {
        setCode('');
        setAnswer({ ok: true, message: redeemSuccessMessage(result, tier) });
        onRedeemed?.({ creditsRemaining: result.creditsRemaining, tier: result.tier });
      } else {
        setAnswer({ ok: false, message: redeemFailureMessage(result.reason) });
      }
    } catch {
      // `redeemCode` answers rather than throws; a press that ends in silence looks unpressed.
      setAnswer({ ok: false, message: redeemFailureMessage('unknown') });
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={(e) => void submit(e)} noValidate aria-label={REDEEM_WORDS.actionLabel}>
      <div className="field-wrap">
        <input
          className="field"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          placeholder={REDEEM_WORDS.field}
          aria-label={REDEEM_WORDS.field}
          autoCapitalize="characters"
          autoCorrect="off"
          autoComplete="off"
          spellCheck={false}
          disabled={busy}
        />
      </div>
      <Button
        type="submit"
        title={REDEEM_WORDS.action}
        accessibilityLabel={REDEEM_WORDS.actionLabel}
        loading={busy}
        disabled={!typed}
      />
      {answer && (
        <p role={answer.ok ? 'status' : 'alert'} className={`type-caption ${answer.ok ? 'plan-detail' : 'field-error-text'}`}>
          <strong>{answer.ok ? REDEEM_WORDS.succeeded : REDEEM_WORDS.failed}</strong>
          <br />
          {answer.message}
        </p>
      )}
    </form>
  );
}
